// Column layout state (order, visibility, widths, pinning) + density + page size, persisted per grid id.
import { useCallback, useEffect, useMemo, useState } from "react";
import type { GridColDef, GridColumnState, GridDensity } from "./types";

interface Persisted {
  columns: GridColumnState;
  density: GridDensity;
  pageSize?: number;
}

const storageKey = (id: string) => `grid:${id}`;

/** Page size the user picked last time for this grid (for server-mode callers that own pagination). */
export function storedPageSize(id: string, options: number[], fallback: number): number {
  const size = read(id).pageSize;
  return size && options.includes(size) ? size : fallback;
}

function read(id: string | undefined): Partial<Persisted> {
  if (!id) return {};
  try {
    const raw = localStorage.getItem(storageKey(id));
    return raw ? (JSON.parse(raw) as Partial<Persisted>) : {};
  } catch {
    return {};
  }
}

function write(id: string | undefined, value: Persisted) {
  if (!id) return;
  try {
    localStorage.setItem(storageKey(id), JSON.stringify(value));
  } catch {
    // storage unavailable — layout lives for this page view only
  }
}

export function defaultColumnState<R>(columns: GridColDef<R>[]): GridColumnState {
  return {
    order: columns.map((c) => c.field),
    hidden: columns.filter((c) => c.hiddenByDefault).map((c) => c.field),
    widths: {},
    pinned: { left: [], right: [] },
  };
}

export function useGridLayout<R>(
  id: string | undefined,
  columns: GridColDef<R>[],
  defaultDensity: GridDensity,
) {
  const fields = columns.map((c) => c.field).join("|");
  const initial = useMemo(() => {
    const saved = read(id);
    return {
      columns: { ...defaultColumnState(columns), ...(saved.columns ?? {}) },
      density: saved.density ?? defaultDensity,
      ...(saved.pageSize ? { pageSize: saved.pageSize } : {}),
    } as Persisted;
    // Re-read only when the grid id or the set of columns changes (`fields` is the stable key).
  }, [id, fields]);
  const [state, setState] = useState<Persisted>(initial);
  useEffect(() => setState(initial), [initial]);
  useEffect(() => write(id, state), [id, state]);

  const known = new Set(columns.map((c) => c.field));
  // Columns added later are appended; removed ones are dropped.
  const order = [
    ...state.columns.order.filter((f) => known.has(f)),
    ...columns.map((c) => c.field).filter((f) => !state.columns.order.includes(f)),
  ];

  const update = useCallback(
    (fn: (c: GridColumnState) => GridColumnState) => setState((s) => ({ ...s, columns: fn(s.columns) })),
    [],
  );
  return {
    columnState: { ...state.columns, order },
    density: state.density,
    pageSize: state.pageSize,
    setDensity: (density: GridDensity) => setState((s) => ({ ...s, density })),
    setPageSize: (pageSize: number) => setState((s) => ({ ...s, pageSize })),
    update,
    reset: () => setState((s) => ({ ...s, columns: defaultColumnState(columns), density: defaultDensity })),
  };
}
